import React, { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { LayoutChangeEvent, TextStyle, useWindowDimensions } from 'react-native'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router'

import { addMinutes } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { LinearGradient } from 'expo-linear-gradient'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { CLOSE_SESSION_BUTTON_TEXT } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { CLOSE_SESSION_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/types'
import { getTimeZoneName } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  DBT_TEENS_SESSION_TYPE_LABELS,
  LTSessionTypes,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  CheckboxButtonType,
  RadioButtonType,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Note, NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ButtonSize, ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import {
  DropdownButton,
  DropdownComponentProps,
  DropdownMenuItem,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { DateField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/DateField.web'
import { RadioButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { AttentionIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AttentionIcon'
import { Avatar } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/Avatar'
import { ChatsIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChatsIcon'
import { loadIconComponent } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/loadIconComponent'
import { getProgramIconName } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/programIcons'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip, TooltipMaxWidth } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { useGetCloseSessionType } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useGetCloseSessionType'
import { InlineBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineBanner/InlineBanner'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  buildCareTimelineItems,
  buildCareTimelineSessionItems,
  CareTimelineAttendance,
  CareTimelineDateFilter,
  CareTimelineFilters,
  CareTimelineItem,
  CareTimelineItemType,
  CareTimelineMilestoneType,
  CareTimelineSort,
  CareTimelineTypeFilter,
  filterAndSortCareTimelineItems,
  getCareTimelineDateRangeBounds,
  getCareTimelineDefaultScope,
  getCareTimelineDisplayRows,
  getCareTimelineMilestoneDescription,
  isCareTimelineDateWithinBounds,
} from './careTimelineUtils'
import { CareTimelineSession } from './data/careTimelineApi'
import { useCareTimelineSessionsData } from './data/hooks/useCareTimelineSessionsData'
import { useCareTimelineActions } from './useCareTimelineActions'
import {
  FilterDropdown,
  FilterMenu,
  FilterOption,
  getFilterDropdownItems,
  RadioFilterMenu,
} from '../../common/components/filterDropdown/FilterDropdown'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { getProviderDetailsData } from '../../providers/data/providerSelectors'
import { useLazyGetNotesQuery } from '../clientNotes/data/notesApi'
import { useDeleteCareReferralDraft } from '../clientNotes/hooks/useDeleteCareReferralDraft'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useGetConcurrentCareQuery } from '../episodes/data/concurrentCareApi'
import { useAllEpisodesForSelectedClient } from '../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

type EpisodeNotesResult =
  | { status: 'success'; episodeId: string; notes: Note[] }
  | { status: 'error'; episodeId: string }

const Page = styled.View({
  alignSelf: 'center',
  width: '100%',
})

const Toolbar = styled.View(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['16px'],
  justifyContent: 'space-between',
  marginBottom: theme.spacing['24px'],
  minHeight: 56,
  zIndex: 7,
}))

const Filters = styled.View(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  flex: 1,
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
  zIndex: 1,
}))

const TimeZoneLabel = styled(BodyText)({
  whiteSpace: 'nowrap',
})

const HistoryList = styled.View({
  width: '100%',
})

const HistoryRow = styled.View<{ isHovered: boolean; hasFollowingItemOnSameDay: boolean; isWideRow: boolean }>(
  ({ theme, isHovered, hasFollowingItemOnSameDay, isWideRow }) => ({
    alignItems: isWideRow ? 'center' : 'stretch',
    backgroundColor: isHovered ? theme.colors.backgroundSecondary : theme.colors.backgroundPrimary,
    borderBottomColor: hasFollowingItemOnSameDay ? 'transparent' : theme.colors.borderDefault,
    borderBottomWidth: 1,
    flexDirection: 'row',
    gap: theme.spacing['16px'],
    paddingHorizontal: theme.spacing['8px'],
    paddingVertical: theme.spacing['16px'],
  }),
)

const MilestoneRow = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundSecondary,
  borderBottomColor: theme.colors.borderDefault,
  borderBottomWidth: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
  justifyContent: 'center',
  paddingHorizontal: theme.spacing['8px'],
  paddingVertical: theme.spacing['12px'],
}))

const HistoryContent = styled.View<{ isWideRow: boolean }>(({ theme, isWideRow }) => ({
  alignItems: isWideRow ? 'center' : 'stretch',
  flex: 1,
  flexDirection: isWideRow ? 'row' : 'column',
  flexWrap: isWideRow ? 'wrap' : 'nowrap',
  gap: isWideRow ? theme.spacing['24px'] : theme.spacing['12px'],
  minWidth: 0,
  position: 'relative',
}))

const DateCard = styled.View<{ showYear: boolean; isWideRow: boolean }>(({ theme, showYear, isWideRow }) => ({
  alignItems: 'center',
  alignSelf: isWideRow ? 'center' : 'flex-start',
  backgroundColor: theme.colors.backgroundSecondary,
  borderColor: theme.colors.borderDefault,
  borderRadius: theme.spacing['8px'],
  borderWidth: 1,
  height: showYear ? 78 : 60,
  justifyContent: 'center',
  width: 56,
}))

const DateSpacer = styled.View<{ isWideRow: boolean }>(({ isWideRow }) => ({
  alignSelf: isWideRow ? 'center' : 'flex-start',
  height: 60,
  width: 56,
}))

const SameDayDivider = styled.View(({ theme }) => ({
  position: 'absolute' as const,
  bottom: 0,
  left: theme.spacing['72px'],
  right: 0,
  height: 1,
  backgroundColor: theme.colors.borderDefault,
}))

const Details = styled.View<{ isWideRow: boolean }>(({ theme, isWideRow }) => ({
  flexShrink: 1,
  gap: theme.spacing['2px'],
  minWidth: 0,
  width: isWideRow ? 300 : '100%',
}))

const SessionMetadata = styled.View(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['4px'],
}))

const Actions = styled.View(({ theme }) => ({
  alignContent: 'center',
  alignItems: 'center',
  bottom: 0,
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
  justifyContent: 'flex-end',
  position: 'absolute' as const,
  right: 0,
  top: 0,
  zIndex: 1,
}))

const NoteActivityContainer = styled.View({
  alignItems: 'center',
  flexDirection: 'row',
})

const NoteActivityGradient = styled(LinearGradient)({
  alignSelf: 'stretch',
  width: 24,
})

const NoteActivityBackground = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  paddingRight: theme.spacing['4px'],
  paddingTop: theme.spacing['2px'],
}))

const NoteActivity = styled(BodyText)({
  whiteSpace: 'nowrap',
})

const NoteLoadError = styled.View(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  gap: theme.spacing['4px'],
}))

const IdentityColumn = styled.View<{ isDisabled?: boolean; width: number; isWideRow: boolean }>(
  ({ theme, isDisabled, width, isWideRow }) => ({
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing['8px'],
    minWidth: 0,
    opacity: isDisabled ? 0.65 : 1,
    ...(isWideRow ? { flexBasis: width, flexGrow: 0, flexShrink: 1 } : { width: '100%' }),
  }),
)

const EmptyState = styled.View(({ theme }) => ({
  alignItems: 'center',
  padding: theme.spacing['40px'],
}))

const LoadingIndicator = styled.ActivityIndicator(({ theme }) => ({
  alignSelf: 'center',
  margin: theme.spacing['40px'],
}))

const NestedFilterMenu = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
  paddingLeft: theme.spacing['32px'],
  paddingRight: theme.spacing['16px'],
}))

const CustomDateFields = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
  paddingLeft: theme.spacing['32px'],
}))

const CustomDateField = styled.View({ width: '100%' })

const DATE_PICKER_OUTSIDE_CLICK_IGNORE_SELECTORS = ['.react-datepicker-popper']

type CheckboxFilterMenuProps = DropdownComponentProps & {
  options: FilterOption<string>[]
  selectedValues: string[]
  testIdPrefix: string
}

const CheckboxFilterMenu: React.FC<CheckboxFilterMenuProps> = ({
  onOptionPress,
  options,
  selectedValues,
  testIdPrefix,
}) => (
  <FilterMenu>
    {options.map((option) => (
      <Checkbox
        buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
        checked={selectedValues.includes(option.value)}
        hideBaseInput
        key={option.value}
        labelSize={BodyTextSize.DEFAULT}
        name={`${testIdPrefix}-${option.value}`}
        onPress={() => onOptionPress(option.value, { id: option.value, selectHandler: noop, text: option.label })}
        testID={tID(`${testIdPrefix}-${option.value}`)}
        title={option.label}
      />
    ))}
  </FilterMenu>
)

type ServiceFilterGroup = {
  label: string
  value: string
  providers: FilterOption<string>[]
}

type ServiceFilterMenuProps = DropdownComponentProps & {
  groups: ServiceFilterGroup[]
  selectedProviders: string[]
  selectedServices: string[]
}

const ServiceFilterMenu: React.FC<ServiceFilterMenuProps> = ({
  groups,
  onOptionPress,
  selectedProviders,
  selectedServices,
}) => (
  <FilterMenu>
    {groups.map((group) => (
      <React.Fragment key={group.value}>
        <Checkbox
          buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
          checked={selectedServices.includes(group.value)}
          hideBaseInput
          name={`CareTimeline-service-${group.value}`}
          onPress={() =>
            onOptionPress(`service:${group.value}`, {
              id: group.value,
              selectHandler: noop,
              text: group.label,
            })
          }
          testID={tID(`CareTimeline-service-option-${group.value}`)}
          title={group.label}
        />
        <NestedFilterMenu>
          {group.providers.map((provider) => (
            <Checkbox
              buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
              checked={selectedProviders.includes(provider.value)}
              hideBaseInput
              key={provider.value}
              name={`CareTimeline-provider-${group.value}-${provider.value}`}
              onPress={() =>
                onOptionPress(`provider:${group.value}:${provider.value}`, {
                  id: provider.value,
                  selectHandler: noop,
                  text: provider.label,
                })
              }
              testID={tID(`CareTimeline-provider-option-${provider.value}`)}
              title={provider.label}
            />
          ))}
        </NestedFilterMenu>
      </React.Fragment>
    ))}
  </FilterMenu>
)

type DateFilterMenuProps = DropdownComponentProps & {
  customDateFrom: string
  customDateTo: string
  fromLabel: string
  onCustomDateFromChange: (value: string) => void
  onCustomDateToChange: (value: string) => void
  options: FilterOption<string>[]
  selectedValue: string
  toLabel: string
}

const DateFilterMenu: React.FC<DateFilterMenuProps> = ({
  customDateFrom,
  customDateTo,
  fromLabel,
  onCustomDateFromChange,
  onCustomDateToChange,
  onOptionPress,
  options,
  selectedValue,
  toLabel,
}) => {
  const { fromMaxDate, toMinDate } = getCareTimelineDateRangeBounds({ customDateFrom, customDateTo })

  return (
    <FilterMenu>
      {options.map((option) => (
        <React.Fragment key={option.value}>
          <RadioButton
            buttonStyle={{ marginBottom: 0 }}
            buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
            gridButtonsGap={0}
            onChange={() => onOptionPress(option.value, { id: option.value, selectHandler: noop, text: option.label })}
            parentContainerWidth={210}
            selected={selectedValue === option.value}
            title={option.label}
            value={option.value}
            widthWrapToContent
          />
          {option.value === CareTimelineDateFilter.CUSTOM_RANGE &&
            selectedValue === CareTimelineDateFilter.CUSTOM_RANGE && (
              <CustomDateFields>
                <CustomDateField testID={tID('CareTimeline-date-from-input')}>
                  <DateField
                    appendFormat={false}
                    createInPortal
                    error=''
                    input={{
                      name: 'CareTimeline-date-from-input',
                      onBlur: noop,
                      onChange: (value: string) => {
                        if (isCareTimelineDateWithinBounds(value, { maxDate: fromMaxDate })) {
                          onCustomDateFromChange(value)
                        }
                      },
                      onFocus: noop,
                      value: customDateFrom,
                    }}
                    inputStyle={{ marginBottom: 0 }}
                    isClearable
                    label={fromLabel}
                    locale='en-US'
                    maxDate={fromMaxDate}
                    placeholder='MM/DD/YYYY'
                    readOnly={false}
                    submitError=''
                    syncExternalChanges
                  />
                </CustomDateField>
                <CustomDateField testID={tID('CareTimeline-date-to-input')}>
                  <DateField
                    appendFormat={false}
                    createInPortal
                    error=''
                    input={{
                      name: 'CareTimeline-date-to-input',
                      onBlur: noop,
                      onChange: (value: string) => {
                        if (isCareTimelineDateWithinBounds(value, { minDate: toMinDate })) {
                          onCustomDateToChange(value)
                        }
                      },
                      onFocus: noop,
                      value: customDateTo,
                    }}
                    inputStyle={{ marginBottom: 0 }}
                    isClearable
                    label={toLabel}
                    locale='en-US'
                    minDate={toMinDate}
                    placeholder='MM/DD/YYYY'
                    readOnly={false}
                    submitError=''
                    syncExternalChanges
                  />
                </CustomDateField>
              </CustomDateFields>
            )}
        </React.Fragment>
      ))}
    </FilterMenu>
  )
}

const INITIAL_FILTERS: CareTimelineFilters = {
  sort: CareTimelineSort.NEWEST,
  service: 'all',
  provider: 'all',
  attendance: [
    CareTimelineAttendance.ATTENDED,
    CareTimelineAttendance.UPCOMING,
    CareTimelineAttendance.CANCELLED,
    CareTimelineAttendance.MISSED,
  ],
  type: [
    CareTimelineTypeFilter.CARE_START_STOP,
    CareTimelineTypeFilter.GENERAL_NOTES,
    CareTimelineTypeFilter.REFERRALS,
    CareTimelineTypeFilter.SESSIONS,
  ],
  date: CareTimelineDateFilter.ANY,
  customDateFrom: '',
  customDateTo: '',
}

const resolveFilterValues = (filter: string[] | 'all', allValues: string[]): string[] =>
  filter === 'all' ? allValues : filter

const normalizeFilterValues = (values: string[], allValues: string[]): string[] | 'all' =>
  values.length === allValues.length && allValues.every((value) => values.includes(value)) ? 'all' : values

const getMultiSelectLabel = (
  options: FilterOption<string>[],
  selectedValues: string[],
  allLabel: string,
  selectedCountLabel: (count: number) => string,
): string => {
  if (selectedValues.length === options.length) {
    return allLabel
  }
  if (selectedValues.length === 1) {
    return options.find((option) => option.value === selectedValues[0])?.label ?? allLabel
  }
  return selectedCountLabel(selectedValues.length)
}

const TEEN_ONLY_SESSION_TYPES = new Set<string>([
  LTSessionTypes.DBT_TEENS_SESSION,
  LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION,
])
const CAREGIVER_ONLY_SESSION_TYPES = new Set<string>([LTSessionTypes.DBT_TEENS_CAREGIVER_SESSION])
const BOTH_NAMES_SESSION_TYPES = new Set<string>([
  LTSessionTypes.DBT_TEENS_INTAKE_SESSION,
  LTSessionTypes.DBT_TEENS_FAMILY_SESSION,
  LTSessionTypes.DBT_TEENS_GROUP_SESSION,
  LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION,
  LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION,
])

const getDbtTeensEffectiveSessionType = (item: CareTimelineItem): string | null | undefined => {
  if (item.sessionType) {
    return item.sessionType
  }
  // Only infer a type when there is no explicit appointment title to preserve: an inferred type is
  // weaker evidence than a backend-confirmed sessionType and must not override a genuine custom title.
  if (!item.hasExplicitTitle && item.programName === ProgramNames.DialecticalBehaviorTherapyTeens) {
    if (item.suggestedSessionType) {
      return item.suggestedSessionType
    }
    if (item.sessionNumber === 1) {
      return LTSessionTypes.DBT_TEENS_INTAKE_SESSION
    }
  }
  return item.sessionType
}

const getDBTTeensNameSuffix = (item: CareTimelineItem): string | undefined => {
  const sessionType = getDbtTeensEffectiveSessionType(item)
  if (!sessionType) {
    return undefined
  }

  if (TEEN_ONLY_SESSION_TYPES.has(sessionType)) {
    return item.teenName || undefined
  }
  if (CAREGIVER_ONLY_SESSION_TYPES.has(sessionType)) {
    return item.caregiverName || undefined
  }
  if (BOTH_NAMES_SESSION_TYPES.has(sessionType)) {
    const names = [item.teenName, item.caregiverName].filter(Boolean).join(', ')
    return names || undefined
  }
  return undefined
}

const getItemTitle = (intl: IntlShape, item: CareTimelineItem): string => {
  const dbtEffectiveType = getDbtTeensEffectiveSessionType(item)
  const dbtLabel = dbtEffectiveType ? DBT_TEENS_SESSION_TYPE_LABELS[dbtEffectiveType] : undefined
  const sessionPrefix = dbtLabel ?? item.title
  const nameSuffix = dbtLabel ? getDBTTeensNameSuffix(item) : undefined

  if (item.attendance === CareTimelineAttendance.CANCELLED) {
    const cancelText = item.cancelName
      ? intl.formatMessage(
          {
            defaultMessage: 'Cancelled by {cancelName}',
            description: 'Cancellation text in Care Timeline showing who cancelled',
          },
          { cancelName: item.cancelName },
        )
      : intl.formatMessage({
          defaultMessage: 'Cancelled',
          description: 'Cancellation text in Care Timeline when canceller is unknown',
        })
    return nameSuffix ? `${sessionPrefix} • ${nameSuffix} • ${cancelText}` : `${sessionPrefix} • ${cancelText}`
  }

  if (item.attendance === CareTimelineAttendance.MISSED) {
    const noShowText = intl.formatMessage({
      defaultMessage: 'No-show',
      description: 'Text for a missed session in Care Timeline',
    })
    return nameSuffix ? `${sessionPrefix} • ${nameSuffix} • ${noShowText}` : `${sessionPrefix} • ${noShowText}`
  }

  if (dbtLabel) {
    return nameSuffix ? `${sessionPrefix} • ${nameSuffix}` : sessionPrefix
  }

  return sessionPrefix
}

const getSessionTime = (item: CareTimelineItem, timeZone: string): string => {
  const startTime = formatInTimeZone(item.date, timeZone, 'h:mm a')
  if (!item.durationMinutes) {
    return startTime
  }
  return `${startTime}–${formatInTimeZone(addMinutes(item.date, item.durationMinutes), timeZone, 'h:mm a')}`
}

const getNoteActivityText = (intl: IntlShape, item: CareTimelineItem, timeZone: string): string | undefined => {
  if (!item.noteActivityDate) {
    return
  }

  const values = {
    providerName: item.providerName,
    date: formatInTimeZone(item.noteActivityDate, timeZone, 'MMM d'),
    time: formatInTimeZone(item.noteActivityDate, timeZone, 'h:mm a'),
  }

  return item.noteStatus === NoteStatusStates.DRAFT
    ? intl.formatMessage(
        {
          defaultMessage: 'Updated {date} • {time}',
          description: 'Timestamp shown when hovering over a Care Timeline item with a draft note',
        },
        values,
      )
    : intl.formatMessage(
        {
          defaultMessage: 'Submitted by {providerName} • {date} • {time}',
          description: 'Timestamp shown when hovering over a Care Timeline item with a completed note',
        },
        values,
      )
}

type CareTimelineItemActionsProps = {
  canManage: boolean
  isDAEngageProvider: boolean
  item: CareTimelineItem
  onAddNote: (item: CareTimelineItem) => void
  onCloseSession: (item: CareTimelineItem) => void
  onCompleteNote: (item: CareTimelineItem) => void
  onDelete?: () => void
  onOpenNote: (item: CareTimelineItem) => void
  canCloseSession: boolean
}

type CloseSessionButtonProps = Pick<CareTimelineItemActionsProps, 'canCloseSession' | 'item' | 'onCloseSession'>

const CloseSessionButton: React.FC<CloseSessionButtonProps> = ({ canCloseSession, item, onCloseSession }) => {
  const intl = useIntl()
  const button = (
    <PrimaryButton
      disabled={!canCloseSession}
      onPress={canCloseSession ? () => onCloseSession(item) : noop}
      size={ButtonSize.SMALL}
      text={intl.formatMessage({
        defaultMessage: 'Close session',
        description: 'Button to close a session in Care Timeline',
      })}
    />
  )

  return canCloseSession ? (
    button
  ) : (
    <Tooltip
      content={intl.formatMessage({
        defaultMessage: 'You must close previous sessions first',
        description:
          'Tooltip shown when a Close session button is disabled because earlier sessions must be closed first',
      })}
      hoverEnabled
      placement='top'
      wrapperStyle={{ flexGrow: 0 }}
      tooltipSize={TooltipMaxWidth.WIDE}
    >
      {button}
    </Tooltip>
  )
}

const CareTimelineItemActions: React.FC<CareTimelineItemActionsProps> = ({
  canManage,
  isDAEngageProvider,
  item,
  onAddNote,
  onCloseSession,
  onCompleteNote,
  onDelete,
  onOpenNote,
  canCloseSession,
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const isSession = item.itemType === CareTimelineItemType.SESSION
  const closeSessionType = useGetCloseSessionType({
    appointmentStatus: item.appointmentStatus,
    engageDAProvider: isDAEngageProvider,
    isInPast: Boolean(item.isPastSession),
    lyraEventType: isSession ? 'session' : '',
    onCloseSession: isSession ? () => onCloseSession(item) : undefined,
    onCompleteNote: isSession ? () => onCompleteNote(item) : undefined,
    onViewCompletedNote: isSession ? () => onOpenNote(item) : undefined,
    sessionNeedsNote: item.noteStatus !== NoteStatusStates.COMPLETED,
    isLegacyDAAppointment: Boolean(item.isLegacyDAAppointment),
    isChargeReversed: Boolean(item.isChargeReversed),
  })

  if (!isSession) {
    if (item.noteStatus === NoteStatusStates.DRAFT) {
      if (!canManage && !onDelete) {
        return null
      }

      const finishLabel =
        item.itemType === CareTimelineItemType.CARE_REFERRAL
          ? intl.formatMessage({
              defaultMessage: 'Finish referral',
              description: 'Button to finish a draft care referral in Care Timeline',
            })
          : intl.formatMessage({
              defaultMessage: 'Finish note',
              description: 'Button to finish a draft note in Care Timeline',
            })

      return (
        <>
          {onDelete && (
            <Link
              text={intl.formatMessage({
                defaultMessage: 'Delete',
                description: 'Link to delete a draft care referral in Care Timeline',
              })}
              accessibilityLabel={intl.formatMessage(
                {
                  defaultMessage: 'Delete {title} draft',
                  description:
                    'Accessibility label for the link that deletes a draft care referral in Care Timeline. title is the row title',
                },
                { title: getItemTitle(intl, item) },
              )}
              onPress={onDelete}
              medium
              style={{ paddingRight: 4 }}
              testID={tID('CareTimeline-delete-draft-link')}
            />
          )}
          {canManage && (
            <SecondaryButton
              isSmall
              onPress={() => onOpenNote(item)}
              text={finishLabel}
              style={{ backgroundColor: colors.backgroundSecondary }}
            />
          )}
        </>
      )
    }

    return item.noteStatus === NoteStatusStates.COMPLETED ? (
      <Link
        text={intl.formatMessage({
          defaultMessage: 'View note',
          description: 'Link to view a completed note in Care Timeline',
        })}
        onPress={() => onOpenNote(item)}
        medium
        style={{ paddingRight: 4 }}
      />
    ) : null
  }

  if (
    canManage &&
    isDAEngageProvider &&
    [
      CLOSE_SESSION_TYPES.INVOICE_NEEDED,
      CLOSE_SESSION_TYPES.INVOICE_AND_NOTE_NEEDED,
      CLOSE_SESSION_TYPES.NOTE_NEEDED,
    ].includes(closeSessionType)
  ) {
    const completesNote = closeSessionType === CLOSE_SESSION_TYPES.NOTE_NEEDED

    return (
      <PrimaryButton
        onPress={() => (completesNote ? onCompleteNote(item) : onCloseSession(item))}
        size={ButtonSize.SMALL}
        text={intl.formatMessage(CLOSE_SESSION_BUTTON_TEXT[closeSessionType as keyof typeof CLOSE_SESSION_BUTTON_TEXT])}
      />
    )
  }

  const showCloseSession = canManage && item.isOpenSession && !isDAEngageProvider

  if (item.noteStatus === NoteStatusStates.COMPLETED) {
    return (
      <>
        <Link
          text={intl.formatMessage({
            defaultMessage: 'View note',
            description: 'Link to view a completed note in Care Timeline',
          })}
          onPress={() => onOpenNote(item)}
          medium
          style={{ paddingRight: 4 }}
        />
        {showCloseSession && (
          <CloseSessionButton canCloseSession={canCloseSession} item={item} onCloseSession={onCloseSession} />
        )}
      </>
    )
  }

  if (item.noteStatus === NoteStatusStates.DRAFT) {
    if (!canManage) {
      return null
    }

    return (
      <>
        <SecondaryButton
          isSmall
          onPress={() => onOpenNote(item)}
          text={intl.formatMessage({
            defaultMessage: 'Finish note',
            description: 'Button to finish a draft note in Care Timeline',
          })}
          style={{ backgroundColor: colors.backgroundSecondary }}
        />
        {showCloseSession && (
          <CloseSessionButton canCloseSession={canCloseSession} item={item} onCloseSession={onCloseSession} />
        )}
      </>
    )
  }

  if (item.attendance === CareTimelineAttendance.CANCELLED || item.attendance === CareTimelineAttendance.MISSED) {
    return null
  }

  if (!canManage) {
    return null
  }

  return (
    <>
      <SecondaryButton
        isSmall
        onPress={() => onAddNote(item)}
        text={intl.formatMessage({
          defaultMessage: 'Add note',
          description: 'Button to add a note to a session in Care Timeline',
        })}
        style={{ backgroundColor: colors.backgroundSecondary }}
      />
      {showCloseSession && (
        <CloseSessionButton canCloseSession={canCloseSession} item={item} onCloseSession={onCloseSession} />
      )}
    </>
  )
}

const CareTimelineMilestone: React.FC<{
  clientFirstName: string
  hasIntakeAssignment: boolean
  item: CareTimelineItem
  onViewDischargeNote: (item: CareTimelineItem, formattedDate: string) => void
  onViewIntake: (item: CareTimelineItem) => void
  timeZone: string
}> = ({ clientFirstName, hasIntakeAssignment, item, onViewDischargeNote, onViewIntake, timeZone }) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const description = getCareTimelineMilestoneDescription(intl, clientFirstName, item)
  const formattedDate = formatInTimeZone(item.date, timeZone, 'MMM d, yyyy')
  const programIconName = getProgramIconName(item.programName)
  const ProgramIcon = programIconName ? loadIconComponent(programIconName) : undefined

  return (
    <MilestoneRow
      accessibilityLabel={intl.formatMessage(
        {
          defaultMessage: '{description}, {date}',
          description: 'Accessibility label for a dated Care Timeline milestone',
        },
        { date: formattedDate, description },
      )}
      testID={tID('CareTimeline-item-milestone')}
    >
      {ProgramIcon ? <ProgramIcon size={24} fillColor={colors.iconDefault} /> : <ChatsIcon />}
      <BodyText color={colors.textPrimary} size={BodyTextSize.SMALL}>
        {description}
      </BodyText>
      <BodyText color={colors.textPrimary} size={BodyTextSize.SMALL}>
        ·
      </BodyText>
      <BodyText color={colors.textPrimary} size={BodyTextSize.SMALL}>
        {formattedDate}
      </BodyText>
      {item.milestoneType === CareTimelineMilestoneType.CARE_STARTED && hasIntakeAssignment && (
        <>
          <BodyText color={colors.textPrimary} size={BodyTextSize.SMALL}>
            ·
          </BodyText>
          <Link
            text={intl.formatMessage({
              defaultMessage: 'View intake',
              description: 'Link to view the intake associated with a Care Timeline milestone',
            })}
            onPress={() => onViewIntake(item)}
            size={BodyTextSize.SMALL}
            underline
          />
        </>
      )}
      {item.dischargeReason && (
        <>
          <BodyText color={colors.textPrimary} size={BodyTextSize.SMALL}>
            ·
          </BodyText>
          <Link
            text={intl.formatMessage({
              defaultMessage: 'View details',
              description: 'Link to view the discharge details associated with a Care Timeline milestone',
            })}
            onPress={() => onViewDischargeNote(item, formattedDate)}
            size={BodyTextSize.SMALL}
            underline
          />
        </>
      )}
    </MilestoneRow>
  )
}

type CareTimelineRowProps = {
  canCloseSession: boolean
  canManage: boolean
  isDAEngageProvider: boolean
  item: CareTimelineItem
  onAddNote: (item: CareTimelineItem) => void
  onCloseSession: (item: CareTimelineItem) => void
  onCompleteNote: (item: CareTimelineItem) => void
  onDelete?: () => void
  onOpenNote: (item: CareTimelineItem) => void
  onWatchRecording: (item: CareTimelineItem) => void
  hasFollowingItemOnSameDay: boolean
  isWideRow: boolean
  showDate: boolean
  showProviderColumn: boolean
  showServiceColumn: boolean
  timeZone: string
}

const CareTimelineRow: React.FC<CareTimelineRowProps> = ({
  canCloseSession,
  canManage,
  hasFollowingItemOnSameDay,
  isDAEngageProvider,
  isWideRow,
  item,
  onAddNote,
  onCloseSession,
  onCompleteNote,
  onDelete,
  onOpenNote,
  onWatchRecording,
  showDate,
  showProviderColumn,
  showServiceColumn,
  timeZone,
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const [isFocusWithin, setIsFocusWithin] = useState(false)
  const cancelledStyle: TextStyle | undefined = item.isCancelled ? { textDecorationLine: 'line-through' } : undefined
  const metadataColor = item.isCancelled ? colors.textInactive : colors.textSecondary
  const isSession = item.itemType === CareTimelineItemType.SESSION
  const isHoverable =
    isSession || [CareTimelineItemType.GENERAL_NOTE, CareTimelineItemType.CARE_REFERRAL].includes(item.itemType)
  const showYear = new Date(item.date).getFullYear() !== new Date().getFullYear()
  const noteActivityText = isHoverable ? getNoteActivityText(intl, item, timeZone) : undefined
  const titleColor =
    item.attendance === CareTimelineAttendance.CANCELLED || item.attendance === CareTimelineAttendance.MISSED
      ? colors.textWarning
      : colors.textPrimary
  const programIconName = getProgramIconName(item.programName)
  const ProgramIcon = programIconName ? loadIconComponent(programIconName) : undefined

  return (
    <HistoryRow
      hasFollowingItemOnSameDay={hasFollowingItemOnSameDay}
      isHovered={isHoverable && (isHovered || isFocusWithin)}
      isWideRow={isWideRow}
      onMouseEnter={isHoverable ? () => setIsHovered(true) : undefined}
      onMouseLeave={isHoverable ? () => setIsHovered(false) : undefined}
      onFocus={isHoverable ? () => setIsFocusWithin(true) : undefined}
      // Focus events bubble on web, so only clear when focus moves out of the row, not between its controls.
      onBlur={
        isHoverable
          ? (event) => {
              const { currentTarget, relatedTarget } = event as unknown as FocusEvent<HTMLElement>
              if (!currentTarget.contains(relatedTarget as Node | null)) {
                setIsFocusWithin(false)
              }
            }
          : undefined
      }
      testID={tID(`CareTimeline-item-${item.itemType}`)}
    >
      {showDate ? (
        <DateCard isWideRow={isWideRow} showYear={showYear}>
          <BodyText
            bold
            color={item.isCancelled ? colors.textInactive : colors.textPrimary}
            size={BodyTextSize.BADGE}
            style={cancelledStyle}
          >
            {formatInTimeZone(item.date, timeZone, 'MMM')}
          </BodyText>
          <Title
            color={item.isCancelled ? colors.textInactive : colors.textPrimary}
            size={TitleSize.MEDIUM}
            style={cancelledStyle}
          >
            {formatInTimeZone(item.date, timeZone, 'd')}
          </Title>
          {showYear && (
            <BodyText
              bold
              color={item.isCancelled ? colors.textInactive : colors.textPrimary}
              size={BodyTextSize.BADGE}
              style={cancelledStyle}
            >
              {formatInTimeZone(item.date, timeZone, 'yyyy')}
            </BodyText>
          )}
        </DateCard>
      ) : (
        <DateSpacer isWideRow={isWideRow} />
      )}
      <HistoryContent isWideRow={isWideRow}>
        <Details isWideRow={isWideRow}>
          <Title color={titleColor} size={TitleSize.XSMALL}>
            {getItemTitle(intl, item)}
          </Title>
          {isSession && (
            <SessionMetadata>
              <BodyText color={metadataColor} size={BodyTextSize.SMALL} style={cancelledStyle}>
                {getSessionTime(item, timeZone)} · {item.detailType}
              </BodyText>
            </SessionMetadata>
          )}
          {isSession &&
            item.attendance === CareTimelineAttendance.ATTENDED &&
            canManage &&
            (item.recordingAvailable ? (
              <Link
                text={intl.formatMessage({
                  defaultMessage: 'Watch recording',
                  description: 'Link to watch a Care Timeline session recording',
                })}
                onPress={() => onWatchRecording(item)}
                size={BodyTextSize.SMALL}
                underline
              />
            ) : (
              <BodyText color={colors.textInactive} size={BodyTextSize.SMALL}>
                {intl.formatMessage({
                  defaultMessage: 'Recording unavailable',
                  description: 'Message shown when a past Care Timeline session does not have a recording',
                })}
              </BodyText>
            ))}
        </Details>
        {showProviderColumn && (
          <IdentityColumn isDisabled={item.isCancelled} isWideRow={isWideRow} width={180}>
            <Avatar
              accessibilityLabel={intl.formatMessage(
                {
                  defaultMessage: 'Photograph of provider {providerName}',
                  description: 'Accessibility label for a provider photograph in Care Timeline',
                },
                { providerName: item.providerName },
              )}
              details={{ displayName: item.providerName, src: item.providerImageUrl || '' }}
              providerID={item.providerId}
              size={24}
            />
            <BodyText color={metadataColor} numberOfLines={1} size={BodyTextSize.SMALL} style={{ flexShrink: 1 }}>
              {item.providerName}
            </BodyText>
          </IdentityColumn>
        )}
        {showServiceColumn && (
          <IdentityColumn isDisabled={item.isCancelled} isWideRow={isWideRow} width={200}>
            {ProgramIcon ? <ProgramIcon size={24} fillColor={colors.iconDefault} /> : <ChatsIcon />}
            <BodyText color={metadataColor} numberOfLines={1} size={BodyTextSize.SMALL} style={{ flexShrink: 1 }}>
              {item.serviceType}
            </BodyText>
          </IdentityColumn>
        )}
        <Actions>
          {item.noteLoadFailed && (
            <NoteLoadError testID={tID('CareTimeline-note-load-error')}>
              <AttentionIcon size={16} fillColor={colors.textWarning} />
              <BodyText color={colors.textWarning} size={BodyTextSize.SMALL}>
                {intl.formatMessage({
                  defaultMessage: 'Unable to retrieve note',
                  description: 'Error status shown on a Care Timeline session row when its note failed to load',
                })}
              </BodyText>
            </NoteLoadError>
          )}
          {(isHovered || isFocusWithin) && noteActivityText && (
            <NoteActivityContainer>
              <NoteActivityGradient
                // Theme colors are 6-digit hex (#RRGGBB); appending 00 sets alpha to 0
                colors={[`${colors.backgroundSecondary}00`, colors.backgroundSecondary]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
              />
              <NoteActivityBackground>
                <NoteActivity
                  color={colors.textSecondary}
                  size={BodyTextSize.SMALL}
                  testID={tID('CareTimeline-note-activity')}
                >
                  {noteActivityText}
                </NoteActivity>
              </NoteActivityBackground>
            </NoteActivityContainer>
          )}
          <CareTimelineItemActions
            canCloseSession={canCloseSession}
            canManage={canManage}
            isDAEngageProvider={isDAEngageProvider}
            item={item}
            onAddNote={onAddNote}
            onCloseSession={onCloseSession}
            onCompleteNote={onCompleteNote}
            onDelete={onDelete}
            onOpenNote={onOpenNote}
          />
        </Actions>
      </HistoryContent>
      {hasFollowingItemOnSameDay && <SameDayDivider />}
    </HistoryRow>
  )
}

const INTAKE_MODAL_MARGIN = 50

const CareTimeline: React.FC = () => {
  const intl = useIntl()
  const theme = useTheme()
  const { height: windowHeight, width: windowWidth } = useWindowDimensions()
  const [isHistoryListWide, setIsHistoryListWide] = useState(windowWidth >= 780)
  const handleHistoryListLayout = useCallback(
    (event: LayoutChangeEvent) => setIsHistoryListWide(event.nativeEvent.layout.width >= 780), // px threshold matching tablet breakpoint
    [],
  )
  const clientId = useSelector(getSelectedClientId)
  const userId = useSelector(getAuthUserId)
  const providerDetails = useSelector(getProviderDetailsData)
  const { data: clientDetails } = useSelectedClientData()
  const { data: appointments, isLoading: isLoadingAppointments } = useClientAppointmentsData(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const { data: careTimelineSessions, isLoading: isLoadingCareTimelineSessions } = useCareTimelineSessionsData({
    refetchOnMountOrArgChange: true,
  })
  const { data: episodes, isLoading: isLoadingEpisodes } = useAllEpisodesForSelectedClient()
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { data: concurrentCare = [] } = useGetConcurrentCareQuery({ patientId: clientId }, { skip: !clientId })
  const [getNotes] = useLazyGetNotesQuery()
  const [noteResults, setNoteResults] = useState<EpisodeNotesResult[] | undefined>()

  const episodeIds = useMemo(() => episodes?.map((episode) => episode.id) ?? [], [episodes])

  const fetchNotes = useCallback(
    (idsToFetch: string[]): Promise<EpisodeNotesResult[]> =>
      Promise.all(
        idsToFetch.map((episodeId) =>
          getNotes({ episodeId }, false)
            .unwrap()
            .then((fetchedNotes): EpisodeNotesResult => ({ status: 'success', episodeId, notes: fetchedNotes }))
            .catch((): EpisodeNotesResult => ({ status: 'error', episodeId })),
        ),
      ),
    [getNotes],
  )

  useEffect(() => {
    let isCurrentRequest = true
    setNoteResults(undefined)

    if (!episodes) {
      return () => {
        isCurrentRequest = false
      }
    }

    fetchNotes(episodeIds).then((results) => {
      if (isCurrentRequest) {
        setNoteResults(results)
      }
    })

    return () => {
      isCurrentRequest = false
    }
  }, [episodeIds, episodes, fetchNotes])

  const notes = useMemo(
    () =>
      noteResults ? noteResults.flatMap((result) => (result.status === 'success' ? result.notes : [])) : undefined,
    [noteResults],
  )

  const noteById = useMemo(() => new Map(notes?.map((note): [string, Note] => [note.id, note])), [notes])

  const { canDelete, requestDelete, confirmationModal } = useDeleteCareReferralDraft({
    onDeleted: (noteId) =>
      setNoteResults((prev) =>
        prev?.map((result) =>
          result.status === 'success'
            ? { ...result, notes: result.notes.filter((note) => note.id !== noteId) }
            : result,
        ),
      ),
  })
  const clientName = [clientDetails?.first_name, clientDetails?.last_name].filter(Boolean).join(' ')

  const getDeleteHandler = (item: CareTimelineItem) => {
    const note = item.noteId ? noteById.get(item.noteId) : undefined
    return note && canDelete(note) ? () => requestDelete(note, clientName) : undefined
  }

  const failedNoteEpisodeIds = useMemo(
    () => new Set(noteResults?.filter((r) => r.status === 'error').map((r) => r.episodeId)),
    [noteResults],
  )

  const hasNoteErrors = failedNoteEpisodeIds.size > 0

  const retryFailedNotes = useCallback(() => {
    const failedIds = Array.from(failedNoteEpisodeIds)
    if (failedIds.length === 0) return

    fetchNotes(failedIds).then((retryResults) => {
      setNoteResults((prev) => {
        if (!prev) return prev
        const retryById = new Map(retryResults.map((r) => [r.episodeId, r]))
        return prev.map((existing) => retryById.get(existing.episodeId) ?? existing)
      })
    })
  }, [failedNoteEpisodeIds, fetchNotes])

  const [filters, setFilters] = useState<CareTimelineFilters>(INITIAL_FILTERS)
  const { setClientDetailsSelectedEpisode } = useOutletContext<{
    setClientDetailsSelectedEpisode?: (episode: Episode) => void
  }>()
  const {
    addAsyncTimeNote,
    addCareReferral,
    addGeneralNote,
    addSessionNote,
    canAddAsyncTimeNote,
    closeCareTimelineSession,
    closeDischargeNoteModal,
    closeIntakeModal,
    completeCareTimelineNote,
    closeRecordingModal,
    dischargeNoteModalContent,
    intakeAssignmentByEpisodeId,
    intakeModalContent,
    isDAEngageProvider,
    isLoadingVideoSessions,
    navigateToExistingNote,
    openDischargeNote,
    openIntakeForm,
    recordingIdByAppointmentId,
    recordingModalContent,
    watchRecording,
  } = useCareTimelineActions({
    appointments: appointments ?? [],
    episodes: episodes ?? [],
    notes: notes ?? [],
    selectedEpisode,
    selectEpisode: setClientDetailsSelectedEpisode,
  })
  const currentDate = useMemo(() => new Date(), [])
  const defaultScopeClientIdRef = useRef<string>()
  const sessionTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  const timeZoneLabel = getTimeZoneName({ timeZone: sessionTimeZone, timeZoneName: 'shortGeneric' }) || sessionTimeZone
  const allFilterLabel = intl.formatMessage({
    defaultMessage: 'All',
    description: 'Filter option to include all Care Timeline items',
  })
  const mySessionsLabel = intl.formatMessage({
    defaultMessage: 'My sessions',
    description: 'Care Timeline provider filter option for the current provider sessions',
  })
  const addMenuItems = useMemo<DropdownMenuItem[]>(
    () => [
      {
        id: CareTimelineItemType.CARE_REFERRAL,
        text: intl.formatMessage({
          defaultMessage: 'Care referral',
          description: 'Option to add a care referral from Care Timeline',
        }),
        selectHandler: addCareReferral,
      },
      {
        id: CareTimelineItemType.GENERAL_NOTE,
        text: intl.formatMessage({
          defaultMessage: 'General note',
          description: 'Option to add a general note from Care Timeline',
        }),
        selectHandler: addGeneralNote,
      },
      ...(canAddAsyncTimeNote
        ? [
            {
              id: 'asyncTimeNote',
              text: intl.formatMessage({
                defaultMessage: 'Async time log',
                description: 'Option to add an async time note from Care Timeline',
              }),
              selectHandler: addAsyncTimeNote,
            },
          ]
        : []),
    ],
    [addAsyncTimeNote, addCareReferral, addGeneralNote, canAddAsyncTimeNote, intl],
  )

  const currentProviderName = useMemo(
    () =>
      providerDetails?.full_name || [providerDetails?.first_name, providerDetails?.last_name].filter(Boolean).join(' '),
    [providerDetails],
  )

  const currentProviderFilterName = useMemo(() => {
    const providerAppointment = appointments?.find(
      (appointment) =>
        appointment.episodeId === selectedEpisode?.id &&
        (appointment.provider?.lyra_id === userId || appointment.lyraProviderId === userId),
    )
    const appointmentProvider = providerAppointment?.provider
    const appointmentProviderName =
      appointmentProvider?.display_name ||
      [appointmentProvider?.display_first_name || appointmentProvider?.first_name, appointmentProvider?.last_name]
        .filter(Boolean)
        .join(' ')

    return appointmentProviderName || currentProviderName
  }, [appointments, currentProviderName, selectedEpisode?.id, userId])

  const providerNameByEpisodeId = useMemo(() => {
    const providerNames = Object.fromEntries(
      concurrentCare.map(({ episodeId, provider }) => [episodeId, provider.name]),
    )

    const episodeProviderIds = new Map(episodes?.map((episode) => [episode.id, episode.provider_id]))
    careTimelineSessions?.forEach((session: CareTimelineSession) => {
      if (
        session.provider.id &&
        session.provider.id === episodeProviderIds.get(session.episodeId) &&
        !providerNames[session.episodeId]
      ) {
        providerNames[session.episodeId] = session.provider.displayName || session.provider.name
      }
    })

    episodes?.forEach((episode) => {
      if (episode.provider_id === userId && currentProviderFilterName) {
        providerNames[episode.id] = currentProviderFilterName
      }
    })

    return providerNames
  }, [careTimelineSessions, concurrentCare, currentProviderFilterName, episodes, userId])

  const serviceNameByEpisodeId = useMemo(
    () =>
      Object.fromEntries(
        (episodes ?? []).map((episode: Episode) => [
          episode.id,
          getProgramDisplayLabel(intl, episode.program_name, 'clientProfile') || episode.program_name,
        ]),
      ),
    [episodes, intl],
  )

  const defaultScope = useMemo(
    () =>
      getCareTimelineDefaultScope({
        episodes: episodes ?? [],
        selectedEpisodeId: selectedEpisode?.id,
        serviceNameByEpisodeId,
        userId,
      }),
    [episodes, selectedEpisode?.id, serviceNameByEpisodeId, userId],
  )

  useEffect(() => {
    if (!clientId || !episodes || defaultScopeClientIdRef.current === clientId) {
      return
    }

    setFilters({
      ...INITIAL_FILTERS,
      provider: defaultScope.provider === 'all' ? 'all' : [defaultScope.provider],
      service: defaultScope.service === 'all' ? 'all' : [defaultScope.service],
    })
    defaultScopeClientIdRef.current = clientId
  }, [clientId, defaultScope, episodes])

  const items = useMemo(() => {
    if (!notes || !episodes) {
      return undefined
    }

    if (careTimelineSessions) {
      return buildCareTimelineSessionItems({
        sessions: careTimelineSessions,
        notes,
        episodes,
        intl,
        clientFirstName: clientDetails?.first_name,
        existingAppointments: appointments,
        recordingIdByAppointmentId,
        serviceNameByEpisodeId,
        currentDate,
        failedNoteEpisodeIds,
      })
    }

    if (appointments) {
      return buildCareTimelineItems({
        appointments,
        notes,
        episodes,
        intl,
        providerNameByEpisodeId,
        recordingIdByAppointmentId,
        serviceNameByEpisodeId,
        currentDate,
        failedNoteEpisodeIds,
      })
    }

    return undefined
  }, [
    appointments,
    careTimelineSessions,
    clientDetails?.first_name,
    currentDate,
    episodes,
    failedNoteEpisodeIds,
    intl,
    notes,
    providerNameByEpisodeId,
    recordingIdByAppointmentId,
    serviceNameByEpisodeId,
  ])

  const oldestOpenSessionIds = useMemo(() => {
    if (!items) {
      return new Set<string>()
    }
    const oldest = new Map<string, CareTimelineItem>()
    for (const item of items) {
      if (!item.isOpenSession || !item.episodeId) {
        continue
      }
      const existing = oldest.get(item.episodeId)
      if (!existing || item.date < existing.date) {
        oldest.set(item.episodeId, item)
      }
    }
    return new Set(Array.from(oldest.values()).map((item) => item.id))
  }, [items])

  const filteredItems = useMemo(
    () => (items ? filterAndSortCareTimelineItems(items, filters, currentDate, sessionTimeZone) : undefined),
    [currentDate, filters, items, sessionTimeZone],
  )
  const displayRows = useMemo(
    () => (filteredItems ? getCareTimelineDisplayRows(filteredItems, sessionTimeZone) : undefined),
    [filteredItems, sessionTimeZone],
  )

  const { showProviderColumn, showServiceColumn } = useMemo(() => {
    if (!filteredItems) {
      return { showProviderColumn: true, showServiceColumn: true }
    }
    const rowItems = filteredItems.filter((item) => item.itemType !== CareTimelineItemType.MILESTONE)
    const isSelfView = rowItems.every((item) => item.providerId === userId)
    const services = new Set(rowItems.filter((item) => item.serviceType !== '—').map((item) => item.serviceType))
    return { showProviderColumn: !isSelfView, showServiceColumn: !isSelfView || services.size > 1 }
  }, [filteredItems, userId])

  const serviceGroups = useMemo<ServiceFilterGroup[]>(() => {
    const providersByService = new Map<string, Map<string, string>>()
    const addProvider = (service: string, providerId?: string, providerName?: string) => {
      if (!service || service === '—') {
        return
      }
      const providers = providersByService.get(service) ?? new Map<string, string>()
      if (providerId && providerName && providerName !== '—') {
        providers.set(providerId, providerName)
      }
      providersByService.set(service, providers)
    }

    Object.values(serviceNameByEpisodeId).forEach((service) => addProvider(service))
    episodes?.forEach((episode) =>
      addProvider(
        serviceNameByEpisodeId[episode.id],
        episode.provider_id,
        providerNameByEpisodeId[episode.id] ||
          (episode.provider_id === userId ? currentProviderFilterName || mySessionsLabel : undefined),
      ),
    )
    items?.forEach((item) => addProvider(item.serviceType, item.providerId || item.providerName, item.providerName))

    return Array.from(providersByService, ([service, providers]) => ({
      label: service,
      value: service,
      providers: Array.from(providers, ([value, label]) => ({ label, value })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    })).sort((a, b) => {
      if (a.value === defaultScope.service) {
        return -1
      }
      if (b.value === defaultScope.service) {
        return 1
      }
      return a.label.localeCompare(b.label)
    })
  }, [
    currentProviderFilterName,
    defaultScope.service,
    episodes,
    items,
    mySessionsLabel,
    providerNameByEpisodeId,
    serviceNameByEpisodeId,
    userId,
  ])

  const allServiceValues = useMemo(() => serviceGroups.map((group) => group.value), [serviceGroups])
  const allProviderValues = useMemo(
    () => Array.from(new Set(serviceGroups.flatMap((group) => group.providers.map((provider) => provider.value)))),
    [serviceGroups],
  )
  const selectedServiceValues = resolveFilterValues(filters.service, allServiceValues)
  const selectedProviderValues = resolveFilterValues(filters.provider, allProviderValues)

  const sortOptions = useMemo<FilterOption<CareTimelineSort>[]>(
    () => [
      {
        label: intl.formatMessage({
          defaultMessage: 'Recent',
          description: 'Care Timeline sort option showing the most recent items first',
        }),
        value: CareTimelineSort.NEWEST,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Oldest',
          description: 'Care Timeline sort option showing the oldest items first',
        }),
        value: CareTimelineSort.OLDEST,
      },
    ],
    [intl],
  )
  const attendanceOptions = useMemo<FilterOption<CareTimelineAttendance>[]>(
    () => [
      {
        label: intl.formatMessage({
          defaultMessage: 'Awaiting Session',
          description: 'Care Timeline attendance filter option for upcoming sessions',
        }),
        value: CareTimelineAttendance.UPCOMING,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Attended',
          description: 'Care Timeline attendance filter option for attended sessions',
        }),
        value: CareTimelineAttendance.ATTENDED,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Cancelled',
          description: 'Care Timeline attendance filter option for cancelled sessions',
        }),
        value: CareTimelineAttendance.CANCELLED,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'No-show',
          description: 'Care Timeline attendance filter option for no-show sessions',
        }),
        value: CareTimelineAttendance.MISSED,
      },
    ],
    [intl],
  )
  const typeOptions = useMemo<FilterOption<CareTimelineTypeFilter>[]>(
    () => [
      {
        label: intl.formatMessage({
          defaultMessage: 'Care start/stop',
          description: 'Care Timeline type filter option for care milestones',
        }),
        value: CareTimelineTypeFilter.CARE_START_STOP,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'General notes',
          description: 'Care Timeline type filter option for general notes',
        }),
        value: CareTimelineTypeFilter.GENERAL_NOTES,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Referrals',
          description: 'Care Timeline type filter option for care referrals',
        }),
        value: CareTimelineTypeFilter.REFERRALS,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Sessions',
          description: 'Care Timeline type filter option for sessions',
        }),
        value: CareTimelineTypeFilter.SESSIONS,
      },
    ],
    [intl],
  )
  const dateOptions = useMemo<FilterOption<CareTimelineDateFilter>[]>(
    () => [
      { label: allFilterLabel, value: CareTimelineDateFilter.ANY },
      {
        label: intl.formatMessage({
          defaultMessage: 'Past week',
          description: 'Care Timeline date filter option for the past week',
        }),
        value: CareTimelineDateFilter.PAST_WEEK,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Past month',
          description: 'Care Timeline date filter option for the past month',
        }),
        value: CareTimelineDateFilter.PAST_MONTH,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Past year',
          description: 'Care Timeline date filter option for the past year',
        }),
        value: CareTimelineDateFilter.PAST_YEAR,
      },
      {
        label: intl.formatMessage({
          defaultMessage: 'Custom range',
          description: 'Care Timeline date filter option for a custom range',
        }),
        value: CareTimelineDateFilter.CUSTOM_RANGE,
      },
    ],
    [allFilterLabel, intl],
  )

  const updateFilter = <K extends keyof CareTimelineFilters>(key: K, value: CareTimelineFilters[K]) => {
    setFilters((currentFilters) => ({ ...currentFilters, [key]: value }))
  }

  const toggleServiceFilter = (service: string) => {
    setFilters((currentFilters) => {
      const selectedServices = resolveFilterValues(currentFilters.service, allServiceValues)
      const selectedProviders = resolveFilterValues(currentFilters.provider, allProviderValues)
      const isSelected = selectedServices.includes(service)
      const nextServices = isSelected
        ? selectedServices.filter((selectedService) => selectedService !== service)
        : [...selectedServices, service]
      const serviceProviderValues =
        serviceGroups.find((group) => group.value === service)?.providers.map((provider) => provider.value) ?? []
      const providersForRemainingServices = new Set(
        serviceGroups
          .filter((group) => nextServices.includes(group.value))
          .flatMap((group) => group.providers.map((provider) => provider.value)),
      )
      const nextProviders = isSelected
        ? selectedProviders.filter(
            (provider) => !serviceProviderValues.includes(provider) || providersForRemainingServices.has(provider),
          )
        : Array.from(new Set([...selectedProviders, ...serviceProviderValues]))

      return {
        ...currentFilters,
        provider: normalizeFilterValues(nextProviders, allProviderValues),
        service: normalizeFilterValues(nextServices, allServiceValues),
      }
    })
  }

  const toggleProviderFilter = (provider: string, service: string) => {
    setFilters((currentFilters) => {
      const selectedProviders = resolveFilterValues(currentFilters.provider, allProviderValues)
      const selectedServices = resolveFilterValues(currentFilters.service, allServiceValues)
      const nextProviders = selectedProviders.includes(provider)
        ? selectedProviders.filter((selectedProvider) => selectedProvider !== provider)
        : [...selectedProviders, provider]
      const serviceProviderValues =
        serviceGroups.find((group) => group.value === service)?.providers.map(({ value }) => value) ?? []
      const hasSelectedProviderForService = serviceProviderValues.some((value) => nextProviders.includes(value))
      const nextServices =
        serviceProviderValues.length > 0 && !hasSelectedProviderForService
          ? selectedServices.filter((selectedService) => selectedService !== service)
          : selectedServices.includes(service)
            ? selectedServices
            : [...selectedServices, service]

      return {
        ...currentFilters,
        provider: normalizeFilterValues(nextProviders, allProviderValues),
        service: normalizeFilterValues(nextServices, allServiceValues),
      }
    })
  }

  const toggleArrayFilter = <T extends CareTimelineAttendance | CareTimelineTypeFilter>(
    key: 'attendance' | 'type',
    value: T,
  ) => {
    setFilters((currentFilters) => {
      const selectedValues = currentFilters[key] as T[]
      return {
        ...currentFilters,
        [key]: selectedValues.includes(value)
          ? selectedValues.filter((selectedValue) => selectedValue !== value)
          : [...selectedValues, value],
      }
    })
  }

  const selectedCountLabel = (count: number) =>
    intl.formatMessage(
      {
        defaultMessage: '{count} selected',
        description: 'Care Timeline filter label when multiple options are selected',
      },
      { count },
    )

  const serviceLabel =
    selectedServiceValues.length === 1
      ? serviceGroups.find((group) => group.value === selectedServiceValues[0])?.label || allFilterLabel
      : selectedServiceValues.length === allServiceValues.length &&
          selectedProviderValues.length === allProviderValues.length
        ? allFilterLabel
        : selectedCountLabel(selectedServiceValues.length)
  const attendanceLabel = getMultiSelectLabel(attendanceOptions, filters.attendance, allFilterLabel, selectedCountLabel)
  const typeLabel = getMultiSelectLabel(typeOptions, filters.type, allFilterLabel, selectedCountLabel)

  const serviceDropdownItems = serviceGroups.flatMap((group) => [
    {
      dropdownOptionText: `service:${group.value}`,
      id: `service:${group.value}`,
      selectHandler: () => toggleServiceFilter(group.value),
      text: group.label,
    },
    ...group.providers.map((provider) => ({
      dropdownOptionText: `provider:${group.value}:${provider.value}`,
      id: `provider:${group.value}:${provider.value}`,
      selectHandler: () => toggleProviderFilter(provider.value, group.value),
      text: provider.label,
    })),
  ])

  const isLoading =
    isLoadingCareTimelineSessions ||
    isLoadingAppointments ||
    isLoadingEpisodes ||
    isLoadingVideoSessions ||
    notes === undefined
  const isAddDisabled = isLoading || !selectedEpisode

  const hasAddEnabledOnceRef = useRef(false)
  if (!isAddDisabled) {
    hasAddEnabledOnceRef.current = true
  }

  return (
    <Page testID={tID('CareTimeline-tab')}>
      <Toolbar>
        <Filters>
          <FilterDropdown
            customDropdownMenuComponent={RadioFilterMenu}
            customDropdownMenuComponentProps={{ options: sortOptions, selectedValue: filters.sort }}
            dropdownItems={getFilterDropdownItems(sortOptions, (sort) => updateFilter('sort', sort))}
            label={intl.formatMessage({
              defaultMessage: 'Sort',
              description: 'Label for the Care Timeline sort filter',
            })}
            selectedLabel={sortOptions.find((option) => option.value === filters.sort)?.label || allFilterLabel}
            shouldCloseMenuOnOptionPress
            testId='CareTimeline-sort-filter'
            zIndex={5}
          />
          <FilterDropdown
            customDropdownMenuComponent={ServiceFilterMenu}
            customDropdownMenuComponentProps={{
              groups: serviceGroups,
              selectedProviders: selectedProviderValues,
              selectedServices: selectedServiceValues,
            }}
            dropdownItems={serviceDropdownItems}
            isHighlighted={filters.service !== 'all' || filters.provider !== 'all'}
            label={intl.formatMessage({
              defaultMessage: 'Service',
              description: 'Label for the Care Timeline service filter',
            })}
            selectedLabel={serviceLabel}
            testId='CareTimeline-service-filter'
            zIndex={4}
          />
          <FilterDropdown
            customDropdownMenuComponent={CheckboxFilterMenu}
            customDropdownMenuComponentProps={{
              options: attendanceOptions,
              selectedValues: filters.attendance,
              testIdPrefix: 'CareTimeline-attendance-option',
            }}
            dropdownItems={getFilterDropdownItems(attendanceOptions, (attendance) =>
              toggleArrayFilter('attendance', attendance),
            )}
            isHighlighted={filters.attendance.length !== attendanceOptions.length}
            label={intl.formatMessage({
              defaultMessage: 'Attendance',
              description: 'Label for the Care Timeline attendance filter',
            })}
            selectedLabel={attendanceLabel}
            testId='CareTimeline-attendance-filter'
            zIndex={3}
          />
          <FilterDropdown
            customDropdownMenuComponent={CheckboxFilterMenu}
            customDropdownMenuComponentProps={{
              options: typeOptions,
              selectedValues: filters.type,
              testIdPrefix: 'CareTimeline-type-option',
            }}
            dropdownItems={getFilterDropdownItems(typeOptions, (type) => toggleArrayFilter('type', type))}
            isHighlighted={filters.type.length !== typeOptions.length}
            label={intl.formatMessage({
              defaultMessage: 'Type',
              description: 'Label for the Care Timeline item type filter',
            })}
            selectedLabel={typeLabel}
            testId='CareTimeline-type-filter'
            zIndex={2}
          />
          <FilterDropdown
            customDropdownMenuComponent={DateFilterMenu}
            customDropdownMenuComponentProps={{
              customDateFrom: filters.customDateFrom,
              customDateTo: filters.customDateTo,
              fromLabel: intl.formatMessage({
                defaultMessage: 'From',
                description: 'Label for the start of a custom Care Timeline date range',
              }),
              onCustomDateFromChange: (value: string) => updateFilter('customDateFrom', value),
              onCustomDateToChange: (value: string) => updateFilter('customDateTo', value),
              options: dateOptions,
              selectedValue: filters.date,
              toLabel: intl.formatMessage({
                defaultMessage: 'To',
                description: 'Label for the end of a custom Care Timeline date range',
              }),
            }}
            dropdownItems={getFilterDropdownItems(dateOptions, (date) => updateFilter('date', date))}
            isHighlighted={filters.date !== CareTimelineDateFilter.ANY}
            label={intl.formatMessage({
              defaultMessage: 'Date',
              description: 'Label for the Care Timeline date filter',
            })}
            outsideClickIgnoreSelectors={DATE_PICKER_OUTSIDE_CLICK_IGNORE_SELECTORS}
            selectedLabel={dateOptions.find((option) => option.value === filters.date)?.label || allFilterLabel}
            testId='CareTimeline-date-filter'
            zIndex={1}
          />
          <TimeZoneLabel color={theme.colors.textSecondary} size={BodyTextSize.SMALL}>
            {intl.formatMessage(
              {
                defaultMessage: 'Session timezone ({timeZone})',
                description: 'Label showing the timezone used for Care Timeline session dates and times',
              },
              { timeZone: timeZoneLabel },
            )}
          </TimeZoneLabel>
        </Filters>
        <DropdownButton
          key={hasAddEnabledOnceRef.current ? 'enabled' : 'disabled'}
          buttonSize={ButtonSize.SMALL}
          buttonType={ButtonType.SECONDARY}
          customButtonText={intl.formatMessage({
            defaultMessage: 'Add',
            description: 'Button to add an item to Care Timeline',
          })}
          customTextColor={isAddDisabled ? theme.colors.textInactive : theme.colors.textActive}
          disableSelectedValueHighlight
          dropdownItems={addMenuItems}
          dropdownStyle={{ right: 0, marginTop: 4 }}
          dropdownWidth={180}
          hideChevron
          isDisabled={isAddDisabled}
          testID={tID('CareTimeline-add-button')}
          style={{
            borderColor: isAddDisabled ? theme.colors.textInactive : theme.colors.textActive,
            borderRadius: 20,
            borderWidth: 1.5,
            height: 40,
            paddingLeft: theme.spacing['12px'],
            paddingRight: theme.spacing['12px'],
          }}
        />
      </Toolbar>
      {hasNoteErrors && (
        <InlineBanner
          testId='CareTimeline-notes-error-banner'
          backgroundColor={theme.colors.components.alertBanner.warning.background}
          textColor={theme.colors.components.alertBanner.warning.text}
          icon={<AttentionIcon size={20} />}
          padding='12px 16px'
          marginBottom='16px'
          textAlign='left'
          text={intl.formatMessage({
            defaultMessage: 'Some notes could not be loaded.',
            description: 'Banner message when some session notes failed to load in Care Timeline',
          })}
          trailingLink={
            <Link
              text={intl.formatMessage({
                defaultMessage: 'Retry',
                description: 'Link to retry loading failed notes in Care Timeline',
              })}
              onPress={retryFailedNotes}
              color={theme.colors.components.alertBanner.warning.text}
              underline
              testID={tID('CareTimeline-notes-retry-link')}
            />
          }
        />
      )}
      {isLoading ? (
        <LoadingIndicator color={theme.colors.textActive} size='large' />
      ) : displayRows?.length ? (
        <HistoryList
          onLayout={handleHistoryListLayout}
          accessibilityLabel={intl.formatMessage(
            {
              defaultMessage: 'Care timeline columns: {columns}, and care milestones',
              description: 'Accessibility label describing the columns in the Care Timeline list',
            },
            {
              columns: [
                intl.formatMessage({ defaultMessage: 'Date', description: 'Care Timeline Date column name' }),
                intl.formatMessage({ defaultMessage: 'Session', description: 'Care Timeline Session column name' }),
                ...(showProviderColumn
                  ? [
                      intl.formatMessage({
                        defaultMessage: 'Provider name',
                        description: 'Care Timeline Provider name column name',
                      }),
                    ]
                  : []),
                ...(showServiceColumn
                  ? [
                      intl.formatMessage({
                        defaultMessage: 'Service type',
                        description: 'Care Timeline Service type column name',
                      }),
                    ]
                  : []),
              ].join(', '),
            },
          )}
          testID={tID('CareTimeline-list')}
        >
          {displayRows.map(({ item, showDate, hasFollowingItemOnSameDay }) =>
            item.itemType === CareTimelineItemType.MILESTONE ? (
              <CareTimelineMilestone
                clientFirstName={
                  clientDetails?.first_name ||
                  intl.formatMessage({
                    defaultMessage: 'Client',
                    description: 'Fallback name for a client in a Care Timeline milestone',
                  })
                }
                hasIntakeAssignment={intakeAssignmentByEpisodeId.has(item.episodeId)}
                item={item}
                key={item.id}
                onViewDischargeNote={openDischargeNote}
                onViewIntake={openIntakeForm}
                timeZone={sessionTimeZone}
              />
            ) : (
              <CareTimelineRow
                canCloseSession={!item.isOpenSession || !item.episodeId || oldestOpenSessionIds.has(item.id)}
                canManage={item.providerId === userId}
                hasFollowingItemOnSameDay={hasFollowingItemOnSameDay}
                isDAEngageProvider={isDAEngageProvider}
                isWideRow={isHistoryListWide}
                item={item}
                key={item.id}
                onAddNote={addSessionNote}
                onCloseSession={closeCareTimelineSession}
                onCompleteNote={completeCareTimelineNote}
                onDelete={getDeleteHandler(item)}
                onOpenNote={navigateToExistingNote}
                onWatchRecording={watchRecording}
                showDate={showDate}
                showProviderColumn={showProviderColumn}
                showServiceColumn={showServiceColumn}
                timeZone={sessionTimeZone}
              />
            ),
          )}
        </HistoryList>
      ) : (
        <EmptyState testID={tID('CareTimeline-empty-state')}>
          <BodyText color={theme.colors.textSecondary}>
            {intl.formatMessage({
              defaultMessage: 'No care timeline items match these filters.',
              description: 'Empty state when no Care Timeline items match the selected filters',
            })}
          </BodyText>
        </EmptyState>
      )}
      <Modal
        visible={recordingModalContent !== null}
        modalContents={recordingModalContent ?? <></>}
        scrollableModalWidth='900px'
        scrollableModalHeight='530px'
        onCloseEnd={closeRecordingModal}
        onRequestClose={closeRecordingModal}
        scrollable
        isHeaderTransparent
        disableBottomSheet
      />
      <Modal
        visible={intakeModalContent !== null}
        modalContents={intakeModalContent ?? <></>}
        onRequestClose={closeIntakeModal}
        onCloseEnd={closeIntakeModal}
        scrollable
        isHeaderTransparent
        scrollableModalHeight='auto'
        closeOnScrim
        scrollableStyle={{ height: windowHeight - INTAKE_MODAL_MARGIN }}
      />
      <Modal
        visible={dischargeNoteModalContent !== null}
        modalContents={dischargeNoteModalContent ?? <></>}
        onRequestClose={closeDischargeNoteModal}
        onCloseEnd={closeDischargeNoteModal}
        scrollable
        isHeaderTransparent
        scrollableModalHeight='auto'
        closeOnScrim
        scrollableStyle={{ maxHeight: windowHeight - INTAKE_MODAL_MARGIN }}
      />
      {confirmationModal}
    </Page>
  )
}

export default CareTimeline
